import {Action, DataContainer, Feature, FeatureStatus, MiniAction, submitEvent, submitMerge, submitMiniAction, submitMove, trackOnNextPageImpression} from "@otto-ec/tracking-bct";
import {TopicpromoCinemaSlide} from "./topicpromo_cinema_slide";
import {getPagecluster} from "@otto-ec/global-resources/misc";

/*                                                                                           */
/*                                                                                                       */
export class TrackingService {
    private slideEventTrackingBuffer: TopicpromoCinemaSlide[] = []

    sendTopicpromoInitialLoadedTrackingEvent(featureOrder: number,
                                             featureIndex: string,
                                             slides: Array<TopicpromoCinemaSlide>,
                                             featureLabels: DataContainer,
                                             masFeatureTrackingLabels: DataContainer): void {
        if (slides.length === 0) {
            return
        }
        const trackingFeatures = this.createTrackingFeatures(featureOrder, featureIndex, featureLabels, masFeatureTrackingLabels, slides, "loaded");
        submitMerge({}, trackingFeatures)
    }

    sendTopicpromoSlideNextTrackingEvent(featureOrder: number, featureIndex: string, slides: Array<TopicpromoCinemaSlide>,
                                         featureLabels: DataContainer, masFeatureTrackingLabels: DataContainer, isStageScrolling: boolean) {
        const slidesForTracking = slides.filter(slide => !slide.isTrackedAsVisible());
        slidesForTracking.forEach(slide => slide.setIsTrackedAsVisible());
        this.slideEventTrackingBuffer.push(...slidesForTracking)

        if (!isStageScrolling) {
            const trackingAction: Action = {
                name: "next",
                features: this.createTrackingFeatures(featureOrder, featureIndex, featureLabels, masFeatureTrackingLabels, this.slideEventTrackingBuffer, "visible")
            }
            if (this.slideEventTrackingBuffer.length > 0) {
                submitEvent({}, trackingAction)
            }
            this.slideEventTrackingBuffer = []
        }
    }

    sendTopicpromoSlidePreviousTrackingEvent(featureOrder: number,
                                             featureIndex: string,
                                             featureLabels: DataContainer,
                                             masFeatureTrackingLabels: DataContainer) {
        const trackingAction: Action = {
            name: "previous",
            features: this.createTrackingFeatures(featureOrder, featureIndex, featureLabels, masFeatureTrackingLabels, [], "visible")
        }
        submitEvent({}, trackingAction)
    }

    sendTopicpromoClickTrackingEvent(featureOrder: number,
                                     featureIndex: string,
                                     slide: TopicpromoCinemaSlide,
                                     featureLabels: DataContainer,
                                     masFeatureTrackingLabels: DataContainer) {
        const featureTrackingData: Action = {
            name: "click",
            features: [
                this.createTopicpromoTrackingFeature(featureOrder, featureIndex, "clicked", featureLabels, masFeatureTrackingLabels),
                this.createTopicpromoTileTrackingFeature(slide.position, featureIndex, "clicked", slide.featureLabels)]
        }
        const labelTrackingData = this.buildClickTrackingLabels(featureLabels, slide.comboType);
        submitMove(labelTrackingData, featureTrackingData)
        trackOnNextPageImpression({"promo_Click": ["TopicPromotionLarge"]})
    }

    private buildClickTrackingLabels(featureTrackingTileLabels: { promo_Source?: string[] }, comboType: string | null): DataContainer {
        let attributionSource: string;
        if (featureTrackingTileLabels.promo_Source) {
            attributionSource = featureTrackingTileLabels.promo_Source[0];
        } else {
            attributionSource = "TopicPromotionLarge";
        }

        const pageCluster = getPagecluster();
        const clickTrackingInfo: DataContainer = {
            "wk.promo_AttributionFeature": [`TopicPromotionLarge_${pageCluster}`],
            "wk.promo_AttributionSource": [attributionSource]
        }
        if (comboType) {
            clickTrackingInfo.promo_ComboType = [comboType];
        }
        return clickTrackingInfo;
    }

    sendMiniAction(featureIndex: string, slidesToTrack: TopicpromoCinemaSlide[]) {
        const miniActions: MiniAction[] = slidesToTrack
            .filter((slide) => !slide.isTrackedAsVisible())
            .map((slide) => {
                slide.setIsTrackedAsVisible()
                return this.createTopicPromoTileFeatureMiniAction(featureIndex, slide.position)
            })
        if (miniActions.length > 0) {
            miniActions.push(this.createTopicPromoFeatureMiniAction(featureIndex))
            submitMiniAction(miniActions)
        }
    }

    private createTrackingFeatures(featureOrder: number, featureIndex: string, featureLabels: DataContainer, masFeatureTrackingLabels: DataContainer, slides: Array<TopicpromoCinemaSlide>, status: FeatureStatus): Feature[] {
        const trackingFeatures = [this.createTopicpromoTrackingFeature(featureOrder, featureIndex, status, featureLabels, masFeatureTrackingLabels)];

        slides.forEach((slide) => {
            const slideTrackingPayload = this.createTopicpromoTileTrackingFeature(slide.position, featureIndex, status, slide.featureLabels);
            trackingFeatures.push(slideTrackingPayload);
        });
        return trackingFeatures;
    }

    private createTopicpromoTrackingFeature(featureOrder: number,
                                            featureIndex: string,
                                            status: FeatureStatus,
                                            featureLabels: DataContainer,
                                            masFeatureTrackingLabels: DataContainer): Feature {
        return {
            id: `ft3_tpr${featureIndex}`,
            name: "TopicPromotionLarge",
            position: featureOrder,
            status: status,
            labels: {...featureLabels, ...masFeatureTrackingLabels}
        }
    }

    private createTopicpromoTileTrackingFeature(position: number, featureIndex: string, status: FeatureStatus, labels: DataContainer): Feature {
        return {
            id: `ft3_tpr${featureIndex}_tile${position}`,
            parentId: `ft3_tpr${featureIndex}`,
            name: "TopicPromotionLarge-Tile",
            position: position,
            status: status,
            labels: labels
        }
    }

    private createTopicPromoFeatureMiniAction(featureIndex: string): MiniAction {
        return {
            featureId: `ft3_tpr${featureIndex}`,
            action: "scroll"
        }
    }

    private createTopicPromoTileFeatureMiniAction(featureIndex: string, position: number): MiniAction {
        return {
            featureId: `ft3_tpr${featureIndex}_tile${position}`,
            action: "scroll"
        }
    }
}